import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  shiftsApi,
  type CreateShiftPayload,
  type UpdateShiftPayload,
} from './api';
import type { ShiftWithTotals } from './schemas';

export const shiftsKeys = {
  all: ['shifts'] as const,
  month: (cnpjId: string | null, month: string, serviceId?: string | null) =>
    ['shifts', 'month', cnpjId, month, serviceId ?? null] as const,
  detail: (id: string) => ['shifts', 'detail', id] as const,
  summary: (id: string) => ['shifts', 'summary', id] as const,
  active: (cnpjId: string | null) => ['shifts', 'active', cnpjId] as const,
};

export function useCalendarMonth(
  cnpjId: string | null,
  month: string,
  serviceId?: string | null,
) {
  return useQuery({
    queryKey: shiftsKeys.month(cnpjId, month, serviceId),
    queryFn: () =>
      shiftsApi.list({
        month,
        cnpjId: cnpjId ?? undefined,
        serviceId: serviceId ?? undefined,
      }),
    enabled: !!cnpjId && !!month,
  });
}

/**
 * Lê um plantão específico a partir do cache do mês; se não estiver em cache,
 * busca o mês correspondente à data do plantão. Fallback: busca direta pelo
 * endpoint de summary + derivar via list do mês atual.
 */
export function useShift(shiftId: string | null, cnpjId: string | null) {
  const qc = useQueryClient();

  return useQuery({
    queryKey: shiftsKeys.detail(shiftId ?? 'noop'),
    enabled: !!shiftId,
    queryFn: async (): Promise<ShiftWithTotals> => {
      // Varrer caches mensais já carregados — evita round-trip.
      const caches = qc.getQueriesData<ShiftWithTotals[]>({
        queryKey: ['shifts', 'month'],
      });
      for (const [, data] of caches) {
        const match = data?.find((s) => s.shift.id === shiftId);
        if (match) return match;
      }
      // Fallback: lista do mês corrente, depois -1 mês. É raro cair aqui
      // (navegação do calendário garante cache quente).
      const now = new Date();
      const ym = (d: Date): string =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const months = [ym(now)];
      months.push(ym(new Date(now.getFullYear(), now.getMonth() - 1, 1)));
      for (const m of months) {
        const list = await shiftsApi.list({ month: m, cnpjId: cnpjId ?? undefined });
        const found = list.find((s) => s.shift.id === shiftId);
        if (found) return found;
      }
      throw new Error('Plantão não encontrado');
    },
  });
}

export function useShiftSummary(shiftId: string | null) {
  return useQuery({
    queryKey: shiftsKeys.summary(shiftId ?? 'noop'),
    queryFn: () => shiftsApi.summary(shiftId!),
    enabled: !!shiftId,
    staleTime: 0,
  });
}

export function useActiveShifts(cnpjId: string | null) {
  const now = new Date();
  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const q = useCalendarMonth(cnpjId, month);
  const active = (q.data ?? []).filter((s) => s.shift.status === 'ACTIVE');
  return { ...q, data: active };
}

export function useShiftMutations(cnpjId: string | null) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: shiftsKeys.all });

  // Aplica o plantão atualizado em TODOS os caches que o referenciam: o detail
  // e cada cache mensal que o contém. Necessário porque `useShift` deriva o
  // detail a partir dos caches mensais; se só atualizássemos o detail, o
  // refetch disparado por `invalidate()` re-leria o mês ainda obsoleto e
  // sobrescreveria o status recém-aplicado (ex.: ACTIVE viraria PENDING de novo).
  const writeShiftToCaches = (data: ShiftWithTotals) => {
    qc.setQueryData(shiftsKeys.detail(data.shift.id), data);
    qc.setQueriesData<ShiftWithTotals[]>(
      { queryKey: ['shifts', 'month'] },
      (list) =>
        list?.map((s) => (s.shift.id === data.shift.id ? data : s)) ?? list,
    );
  };

  const create = useMutation({
    mutationFn: (payload: CreateShiftPayload) => shiftsApi.create(payload),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateShiftPayload }) =>
      shiftsApi.update(id, payload),
    onSuccess: (data) => {
      writeShiftToCaches(data);
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => shiftsApi.remove(id),
    onSuccess: invalidate,
  });

  const start = useMutation({
    mutationFn: (id: string) => shiftsApi.start(id),
    onSuccess: (data) => {
      writeShiftToCaches(data);
      invalidate();
    },
  });

  const end = useMutation({
    mutationFn: (id: string) => shiftsApi.end(id),
    onSuccess: (data) => {
      writeShiftToCaches(data);
      qc.invalidateQueries({ queryKey: shiftsKeys.summary(data.shift.id) });
      invalidate();
    },
  });

  const reopen = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      shiftsApi.reopen(id, reason),
    onSuccess: (data) => {
      writeShiftToCaches(data);
      invalidate();
    },
  });

  // Suprimir aviso de parâmetro.
  void cnpjId;

  return { create, update, remove, start, end, reopen };
}
